<div class="publish theme-light" [ngSwitch]="mode">
  <div class="publish__options text-12" data-test="publishOptions">
    <!-- mode selection -->
    <app-panel [formGroup]="formModeGroup" label="Select a format to publish:">
      <div class="publish__flex-group">
        <mat-radio-group formControlName="mode" [attr.aria-label]="'Publish Format'">
          <mat-radio-button value="3d" class="mr-2">3D Embeddable</mat-radio-button>
          <mat-radio-button value="2d">Image</mat-radio-button>
        </mat-radio-group>
      </div>
    </app-panel>

    <!-- 3D mode-->
    <ng-container *ngSwitchCase="'3d'" [formGroup]="formEmbed">
      <!-- dimensions -->
      <app-panel formGroupName="dimensions" *ngIf="dimensions$ | async as dimensions" label="Size">
        <fieldset class="publish__flex-group" *ngIf="minDimensions$ | async as minDimensions">
          <legend class="visually-hidden">3D Embeddable Dimensions</legend>

          <app-form-field
            [isCompact]="true"
            label="Width"
            theme="light"
            display="block"
            class="mr-6">
            <input
              type="number"
              class="publish__input input input--small"
              formControlName="width"
              [min]="minDimensions.width"
              [attr.aria-label]="'Width'" />
            <div class="mt-1" form-field-help>Min. {{ minDimensions.width }}px</div>
          </app-form-field>

          <app-form-field [isCompact]="true" label="Height" theme="light" display="block">
            <input
              type="number"
              class="publish__input input input--small"
              formControlName="height"
              [min]="minDimensions.height"
              [attr.aria-label]="'Height'" />
            <div class="mt-1" form-field-help>Min. {{ minDimensions.height }}px</div>
          </app-form-field>
        </fieldset>
      </app-panel>

      <!-- quiz -->
      <app-panel
        *ngIf="quizQuestions.length > 0"
        label="Quiz"
        [isCompact]="true"
        [formGroup]="formQuiz">
        <div class="mb-2">
          <mat-checkbox formControlName="locked" data-test="quizLocked"
            >Assessment Mode</mat-checkbox
          >

          <div class="m-1 ml-5" formGroupName="options" data-test="quizOptions">
            <mat-radio-group formControlName="mode" class="flex flex-column">
              <!-- all -->
              <mat-radio-button value="all" data-test="quizOptionsAll"
                >All questions</mat-radio-button
              >

              <!-- single questions -->
              <div class="flex items-center">
                <mat-radio-button value="single" data-test="quizOptionsSingle"
                  >Single question</mat-radio-button
                >

                <select class="input ml-3" formControlName="number" data-test="quizOptionsNumber">
                  <option *ngFor="let question of quizQuestions; let i = index" [value]="i + 1">
                    {{ i + 1 }}
                  </option>
                </select>
              </div>
            </mat-radio-group>
          </div>
        </div>
      </app-panel>

      <!-- display options -->
      <app-panel formGroupName="options" label="Display Options">
        <fieldset class="publish__group">
          <legend class="visually-hidden">Display Options</legend>

          <mat-checkbox formControlName="uiInfo">Information Panel</mat-checkbox>
          <mat-checkbox formControlName="uiAnatomyDescriptions">Anatomy Descriptions</mat-checkbox>
          <mat-checkbox formControlName="uiSearch">Search</mat-checkbox>
          <mat-checkbox formControlName="uiNav">3D Navigation</mat-checkbox>
          <mat-checkbox formControlName="uiTools">Explore Toolbar</mat-checkbox>
          <mat-checkbox formControlName="uiUndo">Undo</mat-checkbox>
          <mat-checkbox formControlName="uiFullscreen">Fullscreen</mat-checkbox>
          <mat-checkbox formControlName="uiHelp">Help</mat-checkbox>
          <mat-checkbox formControlName="uiWhiteboard">Draw</mat-checkbox>
          <mat-checkbox formControlName="uiAnatomyLabels">Anatomy Labels</mat-checkbox>
          <mat-checkbox formControlName="uiLabelList">Label List</mat-checkbox>
          <mat-checkbox formControlName="uiVisibilityLayers" *ngIf="hasVisibilityLayers()"
            >Layers</mat-checkbox
          >
        </fieldset>
      </app-panel>

      <!-- loading options -->
      <app-panel label="Loading Experience">
        <!-- Pre Load Options -->
        <fieldset>
          <legend class="text-10 text-uppercase text-dim mb-2">Pre-Load</legend>

          <div>
            <mat-checkbox formControlName="autoLoad" #autoLoad>Auto-load</mat-checkbox>
          </div>

          <div formGroupName="initial" class="mb-2 ml-5" *ngIf="autoLoad.checked">
            <mat-checkbox formControlName="enabled">Show "Click to Interact" hint</mat-checkbox>
            <div class="m-1 ml-5">
              <mat-radio-group formControlName="mode" class="publish__group">
                <mat-radio-button value="button" class="mv-1">Button</mat-radio-button>
                <mat-radio-button value="handHint" class="mv-1">Animated hand</mat-radio-button>
                <mat-radio-button value="play" class="mv-1">Play</mat-radio-button>
              </mat-radio-group>
            </div>
          </div>

          <div formGroupName="loader" class="mb-2">
            <mat-checkbox formControlName="enabled">Loading Progress Indicator</mat-checkbox>
            <div class="m-1 ml-5">
              <mat-radio-group formControlName="mode" class="publish__group">
                <mat-radio-button value="bar" class="mv-1">Bar</mat-radio-button>
                <mat-radio-button value="circle" class="mv-1">Circle</mat-radio-button>
              </mat-radio-group>
            </div>
          </div>
        </fieldset>

        <!-- Post Load Options -->
        <fieldset>
          <legend class="text-10 text-uppercase text-dim mt-4 mb-2">Post-Load</legend>

          <div formGroupName="rotate" class="mb-1">
            <mat-checkbox formControlName="enabled" #rotate>Rotate 3D Model</mat-checkbox>

            <app-form-field *ngIf="rotate.checked" label="Degrees" theme="light" [isCompact]="true">
              <input
                type="number"
                class="publish__input input"
                formControlName="value"
                [attr.aria-label]="'Degrees'" />
            </app-form-field>
          </div>

          <div formGroupName="options">
            <mat-checkbox formControlName="uiTutorial">Getting Started Tutorial</mat-checkbox>
          </div>

          <div formGroupName="disableScroll">
            <mat-checkbox formControlName="enabled">Disable scroll wheel zooming</mat-checkbox>
            <div class="m-1 ml-5">
              <mat-radio-group formControlName="mode" class="publish__group">
                <mat-radio-button value="alert" class="mv-1">Show alert</mat-radio-button>
                <mat-radio-button value="overlay" class="mv-1">Do not show alert</mat-radio-button>
              </mat-radio-group>
            </div>
          </div>
        </fieldset>
      </app-panel>
    </ng-container>

    <!-- 2D mode -->
    <ng-container *ngSwitchCase="'2d'" [formGroup]="formSnapshot">
      <!-- image size -->
      <app-panel label="Size">
        <app-tabs tabSize="medium" tabTheme="light" #imageTabs>
          <!-- Pixels -->
          <app-tab label="Pixels">
            <button
              app-button
              buttonType="outline"
              buttonTheme="light"
              [uppercase]="false"
              class="mt-2"
              [matMenuTriggerFor]="imageSizes">
              {{ formatScreenSizeText(selectedSnapshotSize) }}
              <app-icon icon="caret-down" class="ml-1"></app-icon>
            </button>

            <mat-menu #imageSizes="matMenu">
              <button
                mat-menu-item
                *ngFor="let snapSize of snapshotSizes"
                (click)="handleSnapshotSizeClick(snapSize)">
                {{ formatScreenSizeText(snapSize) }}
              </button>
            </mat-menu>

            <fieldset class="publish__flex-group mt-2" formGroupName="dimensions">
              <legend class="visually-hidden">Image Dimensions Pixels</legend>

              <app-form-field
                [isCompact]="true"
                label="Width"
                theme="light"
                display="block"
                class="mr-6">
                <input
                  type="number"
                  class="publish__input input input--small"
                  formControlName="width"
                  [min]="minSnapshotPixels.width"
                  [max]="maxSnapshotPixels.width"
                  [attr.aria-label]="'Width'" />
                <div class="mt-1" form-field-help>
                  {{ minSnapshotPixels.width }}px to {{ maxSnapshotPixels.width }}px
                </div>
              </app-form-field>

              <app-form-field [isCompact]="true" label="Height" theme="light" display="block">
                <input
                  type="number"
                  class="publish__input input input--small"
                  formControlName="height"
                  [min]="minSnapshotPixels.height"
                  [max]="maxSnapshotPixels.height"
                  [attr.aria-label]="'Height'" />
                <div class="mt-1" form-field-help>
                  {{ minSnapshotPixels.height }}px to {{ maxSnapshotPixels.height }}px
                </div>
              </app-form-field>
            </fieldset>
          </app-tab>

          <!-- Inches -->
          <app-tab label="Inches">
            <fieldset class="publish__flex-group mt-2" formGroupName="inches">
              <legend class="visually-hidden">Image Dimensions Inches</legend>

              <app-form-field
                [isCompact]="true"
                label="Width"
                theme="light"
                display="block"
                class="mr-6">
                <input
                  type="number"
                  class="publish__input input input--small"
                  formControlName="width"
                  [min]="minSnapshotInches.width"
                  [max]="maxSnapshotInches.width"
                  step=".01"
                  [attr.aria-label]="'Width'" />
                <div class="mt-1" form-field-help>
                  {{ minSnapshotInches.width }}" to {{ maxSnapshotInches.width }}"
                </div>
              </app-form-field>

              <app-form-field
                [isCompact]="true"
                label="Height"
                theme="light"
                display="block"
                class="mr-6">
                <input
                  type="number"
                  class="publish__input input input--small"
                  formControlName="height"
                  [min]="minSnapshotInches.height"
                  [max]="maxSnapshotInches.height"
                  step=".01"
                  [attr.aria-label]="'Height'" />
                <div class="mt-1" form-field-help>
                  {{ minSnapshotInches.height }}" to {{ maxSnapshotInches.height }}"
                </div>
              </app-form-field>
            </fieldset>

            <div class="pt-2 text-small text-dim">Images are published at 300 dpi</div>
          </app-tab>
        </app-tabs>
      </app-panel>

      <!-- image type -->
      <app-panel label="Image Type">
        <div class="publish__flex-group">
          <mat-radio-group formControlName="imageType" [attr.aria-label]="'Image Type'">
            <mat-radio-button
              matTooltip="Smallest file size, best for most uses."
              [value]="imageTypeEnum.JPEG"
              class="mr-2"
              >JPEG</mat-radio-button
            >
            <mat-radio-button
              matTooltip="Highest quality, best for print and offline use."
              [value]="imageTypeEnum.PNG"
              >PNG</mat-radio-button
            >
          </mat-radio-group>
        </div>
      </app-panel>

      <!-- Label Scale -->
      <app-panel label="Label Scale">
        <div class="publish__flex-group">
          <app-slider
            class="flex-grow"
            theme="light"
            formControlName="labelScale"
            min="1"
            max="5"
            step="0.1"
            [label]="'Label scale'"></app-slider>
          <button
            app-button
            buttonType="outline"
            buttonTheme="light"
            class="ml-1"
            (click)="handleResetScale()"
            [attr.aria-label]="'Reset scale'">
            Reset
          </button>
        </div>
      </app-panel>

      <!-- color selection -->
      <app-panel label="Background Color">
        <div class="publish__flex-group">
          <app-color-picker
            size="normal"
            alphaEnabled="false"
            [color]="snapshotBackgroundColor"
            (colorSelected)="handleSnapshotBackgroundSelected($event)"
            class="publish__picker">
          </app-color-picker>

          <button
            app-button
            buttonType="outline"
            buttonTheme="light"
            (click)="handleSnapshotBackgroundSelected(null)"
            [attr.aria-label]="'Reset background color'">
            Reset
          </button>
        </div>
      </app-panel>

      <div class="mt-4">
        <a href="https://www.biodigital.com/terms" target="_blank" class="text-link text-underline"
          >Read about our Terms of Use</a
        >
      </div>
    </ng-container>
  </div>

  <!-- actions -->
  <div class="publish__actions" data-test="publishActions">
    <!-- 3D options -->
    <ng-container *ngSwitchCase="'3d'">
      <p class="text-12 p-2 mt-2 mb-2">
        Make this model available to anyone by publishing it to the web.
      </p>

      <div class="publish__actions-content">
        <button
          class="mb-2 w-100"
          app-button
          buttonType="flat"
          buttonTheme="light"
          (click)="handleGenerateIFrame()"
          [disabled]="!this.formEmbed.valid"
          data-test="copyEmbed3D">
          Copy embed
        </button>
        <button
          class="w-100"
          app-button
          buttonType="flat"
          buttonTheme="light"
          (click)="handleGenerateURL()"
          [disabled]="!this.formEmbed.valid"
          data-test="copyURL3D">
          Copy URL
        </button>
      </div>
    </ng-container>

    <!-- 2D options -->
    <ng-container *ngSwitchCase="'2d'">
      <p class="text-12 p-2 mt-2 mb-2">
        Download this image or make it available to anyone by publishing it to the web.
      </p>

      <div class="publish__actions-content">
        <button
          class="mb-2 w-100"
          app-button
          buttonType="flat"
          buttonTheme="light"
          (click)="handleDownloadSnapClick()"
          [disabled]="!this.formSnapshot.valid"
          data-test="download2D">
          Download
        </button>
        <button
          class="mb-2 w-100"
          app-button
          buttonType="flat"
          buttonTheme="light"
          (click)="handleCopyImageURL()"
          [disabled]="!this.formSnapshot.valid"
          data-test="copyURL2D">
          Copy URL
        </button>
        <button
          class="w-100"
          app-button
          buttonType="flat"
          buttonTheme="light"
          (click)="handleGenerateImage()"
          [disabled]="!this.formSnapshot.valid"
          data-test="copyEmbed2D">
          Copy Embed
        </button>
      </div>
    </ng-container>
  </div>

  <!-- widget -->
  <!-- <app-publish-widget
      #publishWidget
      class="publish__embed"
      [model]="model"
      [widgetParams]="widgetOptions"
      [width]="widgetWidth"
      [height]="widgetHeight"
      [bgColor]="widgetBGColor"
      [scalable]="widgetScalable"
      [enableUI]="mode === '3d'"
      [labelScale]="labelScale"
      (cameraUpdated)="handleCameraUpdated($event)">
    </app-publish-widget> -->

  <app-publish-widget
    #publishWidget
    class="publish__embed"
    [model]="model"
    [options]="publishWidgetOptions">
  </app-publish-widget>
</div>
